import React, { useState, useEffect, Fragment } from 'react';
import { Button, Form, Card, Alert, Row, Col, Table, InputGroup, Modal } from 'react-bootstrap';
import { Save, X, Plus, Minus, Truck, User, Tag, DollarSign, UserPlus } from 'lucide-react';
import useForm from '../../hooks/useForm';
import { createOrder } from '../../api/orders';
import { getAllProducts } from '../../api/products';
import { getAllSuppliers, createSupplier } from '../../api/partner';
import useAuth from '../../hooks/useAuth';


const OrderForm = ({ onCancel, onSuccess }) => {
    const { isAdmin, isManager, isSalesRep, user, isDistributor } = useAuth();
    const hasStockAccess = isAdmin || isManager || isSalesRep;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [clients, setClients] = useState([]);
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([{ productId: '', quantity: 1, customPrice: null }]);

    // État pour le client saisi manuellement
    const [clientInput, setClientInput] = useState('');
    const [selectedClientId, setSelectedClientId] = useState('');
    const [showCreateClientModal, setShowCreateClientModal] = useState(false);
    const [newClientName, setNewClientName] = useState('');
    const [creatingClient, setCreatingClient] = useState(false);

    const initialValues = {
        partnerId: '',
        notes: '',
        deliveryAddress: ''
    };

    const { values, handleChange, errors, validateForm, setValues } = useForm(initialValues);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const productsData = await getAllProducts();
                setProducts(productsData);
            } catch (err) {
                setError('Failed to load data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        const fetchClients = async () => {
            try {
                const clientsData = await getAllSuppliers();
                setClients(clientsData);
            } catch (err) {
                setError('Failed to load clients.');
            }
        };
        fetchClients();
    }, []);

    const handleProductChange = (index, field, value) => {
        const updatedProducts = [...selectedProducts];
        updatedProducts[index][field] = value;
        setSelectedProducts(updatedProducts);
    };

    const handleAddProduct = () => {
        setSelectedProducts([...selectedProducts, { productId: '', quantity: 1, customPrice: null }]);
    };

    const handleRemoveProduct = (index) => {
        if (selectedProducts.length <= 1) return;
        const updatedProducts = [...selectedProducts];
        updatedProducts.splice(index, 1);
        setSelectedProducts(updatedProducts);
    };

    const getProductPrice = (productId, index) => {
        // Vérifier d'abord si un prix personnalisé a été défini et qu'il n'est pas vide
        if (selectedProducts[index] &&
            selectedProducts[index].customPrice !== null &&
            selectedProducts[index].customPrice !== '') {
            return parseFloat(selectedProducts[index].customPrice) || 0; // Convertir en nombre et s'assurer qu'il n'est pas NaN
        }

        // Si pas de prix personnalisé valide, utiliser le prix régulier du produit
        const productIdNumber = Number(productId);
        const product = products.find(p => p.id === productIdNumber);
        return product && product.regular_price ? product.regular_price : 0;
    };

    const getProductTotal = (productId, quantity, index) => {
        return getProductPrice(productId, index) * quantity;
    };

    const getOrderTotal = () => {
        return selectedProducts.reduce((acc, item, index) => {
            const total = getProductTotal(item.productId, item.quantity, index);
            return acc + total;
        }, 0);
    };

    const handleClientInputChange = (e) => {
        const inputValue = e.target.value;
        setClientInput(inputValue);

        // Vérifie si le client existe déjà
        const foundClient = clients.find(client =>
            client.name.toLowerCase() === inputValue.toLowerCase()
        );

        if (foundClient) {
            setSelectedClientId(foundClient.id);
            setValues({ ...values, partnerId: foundClient.id });
        } else {
            setSelectedClientId('');
            setValues({ ...values, partnerId: '' });
        }
    };

    const handleCreateNewClient = async () => {
        if (!newClientName.trim()) {
            return;
        }

        try {
            setCreatingClient(true);
            // Appel API pour créer un nouveau client avec le nom minimum requis
            const newClient = await createSupplier({
                name: newClientName,
                type: 'CLIENT'
                // Vous pouvez ajouter d'autres champs par défaut si nécessaire
            });

            // Mise à jour de la liste des clients
            setClients([...clients, newClient]);

            // Sélection du nouveau client
            setClientInput(newClient.name);
            setSelectedClientId(newClient.id);
            setValues({ ...values, partnerId: newClient.id });

            setShowCreateClientModal(false);
            setNewClientName('');
        } catch (err) {
            setError('Échec de création du client: ' + (err.message || 'Erreur inconnue'));
        } finally {
            setCreatingClient(false);
        }
    };

    const validationSchema = {
        partnerId: {
            required: true,
            errorMessage: 'Le client est requis'
        },
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        // Si un nom de client a été saisi mais qu'il n'existe pas encore, proposer de le créer
        if (clientInput && !selectedClientId) {
            setNewClientName(clientInput);
            setShowCreateClientModal(true);
            setLoading(false);
            return;
        }

        const isValid = validateForm(validationSchema);
        if (!isValid) {
            setLoading(false);
            return;
        }

        const validProducts = selectedProducts.filter(item => item.productId && Number(item.quantity) > 0);

        if (validProducts.length === 0) {
            setError('Veuillez ajouter au moins un produit à la commande');
            setLoading(false);
            return;
        }

        const items = validProducts.map((item, index) => {
            // S'assurer que le prix n'est jamais null
            const price = getProductPrice(item.productId, index);
            return {
                productId: Number(item.productId),
                quantity: Number(item.quantity),
                price: price > 0 ? price : 0, // Garantir que le prix n'est jamais null ou négatif
                unitPrice: price > 0 ? price : 0 // Ajouter explicitement unitPrice pour correspondre au modèle Java
            };
        });

        const orderData = {
            partnerId: Number(values.partnerId),
            partnerType: isDistributor ? 'CLIENT' : values.partnerType,
            type: isDistributor ? 'CLIENT' : values.partnerType,
            deliveryAddress: values.deliveryAddress,
            notes: values.notes,
            orderDate: new Date().toISOString(),
            deliveryDate: new Date().toISOString(),
            items
        };

        try {
            await createOrder(orderData);
            setSuccess('Commande créée avec succès!');
            setTimeout(() => onSuccess && onSuccess(), 1500);
        } catch (err) {
            setError(err.message || 'Échec de création de la commande. Veuillez réessayer.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Card className="shadow-sm">
                <Card.Header><h5>Créer nouvelle commande</h5></Card.Header>
                <Card.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && <Alert variant="success">{success}</Alert>}

                    <Form onSubmit={handleSubmit}>
                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>Client</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text><User size={18} /></InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            value={clientInput}
                                            onChange={handleClientInputChange}
                                            placeholder="Rechercher ou saisir le nom du client"
                                            list="clients-list"
                                            required
                                        />
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => {
                                                setNewClientName(clientInput);
                                                setShowCreateClientModal(true);
                                            }}
                                        >
                                            <UserPlus size={18} />
                                        </Button>
                                    </InputGroup>
                                    <datalist id="clients-list">
                                        {clients.map(client => (
                                            <option key={client.id} value={client.name} />
                                        ))}
                                    </datalist>
                                    {!selectedClientId && clientInput && (
                                        <Form.Text className="text-muted">
                                            <h6>Client non trouvé. Cliquez sur + pour créer un nouveau client.</h6>

                                        </Form.Text>
                                    )}
                                    {errors.partnerId && <Form.Text className="text-danger">{errors.partnerId}</Form.Text>}
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>Adresse de livraison</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text><Truck size={18} /></InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            name="deliveryAddress"
                                            value={values.deliveryAddress}
                                            onChange={handleChange}
                                            placeholder="Entrer l'adresse de livraison"
                                        />
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Notes</Form.Label>
                            <Form.Control
                                as="textarea"
                                name="notes"
                                value={values.notes}
                                onChange={handleChange}
                                placeholder="Ajouter une note"
                            />
                        </Form.Group>

                        <Card className="mb-4">
                            <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                                <h6>Produits de la commande</h6>
                                <Button variant="outline-primary" size="sm" onClick={handleAddProduct}>
                                    <Plus size={16} className="me-1" /> Ajouter articles
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                <Table bordered>
                                    <thead>
                                        <tr>
                                            <th>Produits</th>
                                            <th>Prix Unitaire</th>
                                            <th>Quantité</th>
                                            <th>Total</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedProducts.map((item, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <Form.Control
                                                        type="text"
                                                        value={item.productName || ''}
                                                        onChange={(e) => {
                                                            const searchTerm = e.target.value;

                                                            const productNameMatch = searchTerm.match(/^(.+?) -/);
                                                            const productName = productNameMatch ? productNameMatch[1].trim() : searchTerm;

                                                            // Cherche le produit par nom
                                                            const foundProduct = products.find(p =>
                                                                p.name.toLowerCase() === productName.toLowerCase()
                                                            );

                                                            if (foundProduct) {
                                                                // Mettre à jour l'ID du produit et le prix unitaire
                                                                handleProductChange(index, 'productId', foundProduct.id);
                                                                handleProductChange(index, 'productName', searchTerm);
                                                                // S'assurer que le prix n'est jamais null
                                                                const initialPrice = foundProduct.regular_price || 0;
                                                                handleProductChange(index, 'customPrice', initialPrice);
                                                            } else {
                                                                // Mettre à jour seulement le terme de recherche sans changer l'ID
                                                                handleProductChange(index, 'productName', searchTerm);
                                                                handleProductChange(index, 'productId', '');
                                                            }
                                                        }}
                                                        list="products-list"
                                                        placeholder="Commencez à taper le nom du produit"
                                                        required
                                                    />
                                                    <datalist id="products-list">
                                                        {products.map(product => (
                                                            <option
                                                                key={product.id}
                                                                value={`${product.name} - ${product.sku}`}
                                                            />
                                                        ))}
                                                    </datalist>
                                                </td>

                                                <td>
                                                    <InputGroup>
                                                        <Form.Control
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            value={item.customPrice !== null ? item.customPrice : ''}
                                                            onChange={(e) => handleProductChange(index, 'customPrice', e.target.value)}
                                                            onWheel={(e) => e.target.blur()}
                                                            placeholder="Prix unitaire"
                                                            required
                                                        />
                                                    </InputGroup>
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="number"
                                                        min="1"
                                                        value={item.quantity}
                                                        onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                                                        onWheel={(e) => e.target.blur()}
                                                        required
                                                    />
                                                </td>
                                                <td>{getProductTotal(item.productId, item.quantity, index).toFixed()} F cfa</td>
                                                <td>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => handleRemoveProduct(index)}
                                                        disabled={selectedProducts.length <= 1}
                                                    >
                                                        <Minus size={16} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <div className="text-end mt-3">
                                    <h6>Total : <strong>{getOrderTotal().toFixed()} F cfa</strong></h6>
                                </div>

                            </Card.Body>
                        </Card>

                        <div className="d-flex justify-content-end">
                            <Button type="submit" variant="primary" disabled={loading}>
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Traitement...
                                    </>
                                ) : (
                                    <>
                                        <Save size={18} className="me-1" /> Enregistrer commande
                                    </>
                                )}
                            </Button>
                            <Button variant="secondary" className="ms-2" onClick={onCancel}>
                                <X size={18} className="me-1" /> Annuler
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            {/* Modal pour créer un nouveau client */}
            <Modal show={showCreateClientModal} onHide={() => setShowCreateClientModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Créer un nouveau client</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Nom du client</Form.Label>
                        <Form.Control
                            type="text"
                            value={newClientName}
                            onChange={(e) => setNewClientName(e.target.value)}
                            placeholder="Entrer le nom du client"
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateClientModal(false)}>
                        Annuler
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleCreateNewClient}
                        disabled={creatingClient || !newClientName.trim()}
                    >
                        {creatingClient ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Création...
                            </>
                        ) : (
                            'Créer et sélectionner'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default OrderForm;